import React, { useEffect, useMemo, useState } from "react";
import {
  AutoComplete,
  Button,
  Flex,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Switch,
  Typography,
  message,
} from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { getErrorMessage } from "@/utilities";
import { storePopulationsApi, StoreSeller } from "@/services/store-populations.api";
import { AGENT_PROVIDERS, AgentConfig, agentApi } from "./api";
import {
  FormularioDelAsistente,
  MODELOS_SUGERIDOS,
  cambiosParaGuardar,
  faltaParaEncender,
  fecha,
  formularioDesde,
} from "./agent-config";
import { PROVEEDORES } from "./asistentes";

const { Text } = Typography;

interface Props {
  storeCode: string;
  /** El usuario asistente (D62): la configuración es suya, no del store. */
  userId: number;
  editable: boolean;
  /** Para que la lista de asistentes refleje lo guardado: encendido, modelo, key. */
  onGuardado?: () => void;
}

/**
 * Configuración de un asistente (tareas 5.1 y 5.11): activo, nombre, proveedor, modelo, API key de
 * sólo escritura, seller preferido y límites. Guarda sólo lo que cambió. El consumo y el fusible
 * están en su propia pestaña.
 */
export const ConfiguracionTab: React.FC<Props> = ({ storeCode, userId, editable, onGuardado }) => {
  const [form] = Form.useForm<FormularioDelAsistente>();
  const [config, setConfig] = useState<AgentConfig | null>(null);
  const [sellers, setSellers] = useState<StoreSeller[]>([]);
  const [saving, setSaving] = useState(false);
  const proveedor = Form.useWatch("provider", form);
  const valores = Form.useWatch([], form);

  const inicial = useMemo(() => formularioDesde(config), [config]);
  const ahora = { ...inicial, ...valores };
  const hayCambios = Object.keys(cambiosParaGuardar(inicial, ahora)).length > 0;
  const falta = ahora.enabled ? faltaParaEncender(ahora, !!config?.hasApiKey) : [];

  useEffect(() => {
    agentApi
      .config(storeCode, userId)
      .then((c) => {
        setConfig(c);
        form.setFieldsValue(formularioDesde(c));
      })
      .catch((e) => message.error(getErrorMessage(e)));
    storePopulationsApi.listSellers(storeCode).then(setSellers).catch(() => setSellers([]));
  }, [storeCode, userId, form]);

  const guardar = async () => {
    const cambios = cambiosParaGuardar(inicial, ahora);
    setSaving(true);
    try {
      const c = await agentApi.guardar(storeCode, userId, cambios);
      setConfig(c);
      form.setFieldsValue(formularioDesde(c));
      onGuardado?.();
      message.success("Configuración guardada. El asistente la toma en menos de un minuto.");
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const borrarKey = async () => {
    setSaving(true);
    try {
      // Sin key no puede quedar encendido: se apaga en el mismo guardado.
      const c = await agentApi.guardar(storeCode, userId, { apiKey: null, enabled: false });
      setConfig(c);
      form.setFieldsValue(formularioDesde(c));
      onGuardado?.();
      message.success("API key borrada y asistente apagado");
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form form={form} layout="vertical" disabled={!editable} initialValues={inicial} style={{ maxWidth: 640 }}>
      <Form.Item
        name="enabled"
        label="Activo"
        valuePropName="checked"
        extra="Apagado, el asistente no lee ni responde en ningún canal."
        validateStatus={falta.length ? "error" : undefined}
        help={falta.length ? `Para encenderlo falta ${falta.join(", ")}` : undefined}
      >
        <Switch />
      </Form.Item>

      <Form.Item name="displayName" label="Nombre" extra="Con este nombre firma, se lo arroba en las notas y se lo elige en los canales.">
        <Input maxLength={80} placeholder="Asistente del Store" />
      </Form.Item>

      <Flex gap={16} wrap="wrap">
        <Form.Item name="provider" label="Proveedor" style={{ minWidth: 180 }}>
          <Select options={AGENT_PROVIDERS.map((p) => ({ value: p, label: PROVEEDORES[p] ?? p }))} />
        </Form.Item>
        <Form.Item
          name="model"
          label="Modelo"
          style={{ flex: 1, minWidth: 260 }}
          rules={[{ pattern: /^[A-Za-z0-9._:/@-]{1,100}$/, message: "Sin espacios ni caracteres raros" }]}
        >
          <AutoComplete
            options={MODELOS_SUGERIDOS[proveedor ?? ""] ?? []}
            placeholder="claude-sonnet-5-5"
            filterOption={(input, o) => String(o?.value ?? "").includes(input.trim())}
          />
        </Form.Item>
      </Flex>

      <Form.Item
        label="API key"
        extra={
          config?.hasApiKey
            ? `Guardada: ${config.apiKeyHint ?? "••••"}. Escribí una nueva sólo para reemplazarla.`
            : "Se guarda cifrada y no se vuelve a mostrar."
        }
      >
        <Flex gap={8}>
          <Form.Item name="apiKey" noStyle>
            <Input.Password
              autoComplete="new-password"
              placeholder={config?.hasApiKey ? config.apiKeyHint ?? "••••" : "sk-…"}
              visibilityToggle={false}
            />
          </Form.Item>
          {config?.hasApiKey && editable && (
            <Popconfirm
              title="¿Borrar la API key?"
              description="El asistente se apaga hasta que cargues otra."
              okText="Borrar"
              okButtonProps={{ danger: true }}
              cancelText="Cancelar"
              onConfirm={borrarKey}
            >
              <Button danger>Borrar</Button>
            </Popconfirm>
          )}
        </Flex>
      </Form.Item>

      <Form.Item
        name="preferredSeller"
        label="Seller preferido"
        extra="Si un producto lo venden varios sellers, el presupuesto sale con éste."
      >
        <Select
          allowClear
          placeholder="El que resuelva el Store"
          options={sellers.map((s) => ({ value: s.sellerCode, label: s.name }))}
        />
      </Form.Item>

      <Text strong style={{ display: "block", marginBottom: 8 }}>Límites</Text>
      <Flex gap={16} wrap="wrap">
        <Form.Item name="maxTokensPerDay" label="Tokens por día" style={{ width: 200 }}>
          <InputNumber min={1000} max={100_000_000} step={100_000} style={{ width: "100%" }} placeholder="Por defecto: sin tope" />
        </Form.Item>
        <Form.Item name="maxToolCallsPerTurn" label="Herramientas por llamada" style={{ width: 200 }}>
          <InputNumber min={1} max={50} style={{ width: "100%" }} placeholder="Por defecto: 12" />
        </Form.Item>
        <Form.Item name="maxTurnsPerConversation" label="Llamadas por conversación" style={{ width: 200 }}>
          <InputNumber min={1} max={500} style={{ width: "100%" }} placeholder="Por defecto: sin tope" />
        </Form.Item>
        <Form.Item name="ratePerMin" label="Pedidos por minuto" style={{ width: 200 }}>
          <InputNumber min={10} max={500} style={{ width: "100%" }} placeholder={`Por defecto: ${config?.defaults.ratePerMin ?? 120}`} />
        </Form.Item>
      </Flex>

      {config?.updatedAt && (
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Última modificación: {fecha(config.updatedAt)}
        </Text>
      )}

      {editable && (
        <Button type="primary" icon={<CheckOutlined />} iconPosition="end" loading={saving} disabled={!hayCambios || falta.length > 0} onClick={guardar}>
          Guardar
        </Button>
      )}
    </Form>
  );
};
